import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TimeTrackingWidget = ({ jobs }) => {
  const [selectedJob, setSelectedJob] = useState('');
  const [date, setDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [hours, setHours] = useState('');
  const [description, setDescription] = useState('');
  const [recentEntries, setRecentEntries] = useState([
    {
      id: 1,
      jobTitle: 'Warmtepomp Installatie',
      date: '2026-01-14',
      hours: 6.5,
      rate: 45.00,
      earnings: 292.50,
      description: 'Installatie en configuratie warmtepomp systeem'
    },
    {
      id: 2,
      jobTitle: 'Airco Onderhoud',
      date: '2026-01-13',
      hours: 4.0,
      rate: 42.00,
      earnings: 168.00,
      description: 'Jaarlijks onderhoud en controle airco units'
    },
    {
      id: 3,
      jobTitle: 'Zonnepanelen Installatie',
      date: '2026-01-12',
      hours: 8.0,
      rate: 48.00,
      earnings: 384.00,
      description: 'Montage 12 zonnepanelen op plat dak'
    }
  ]);

  const jobOptions = jobs?.map(job => ({
    value: job?.id,
    label: `${job?.title} - €${job?.rate?.toFixed(2)}/${job?.rateType === 'hourly' ? 'uur' : 'project'}`,
    description: job?.client
  }));

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!selectedJob || !hours) return;

    const job = jobs?.find(j => j?.id === selectedJob);
    const hoursNum = parseFloat(hours);
    const earnings = job?.rateType === 'hourly' ? hoursNum * job?.rate : job?.rate;

    const newEntry = {
      id: Date.now(),
      jobTitle: job?.title,
      date,
      hours: hoursNum,
      rate: job?.rate,
      earnings,
      description
    };

    setRecentEntries([newEntry, ...recentEntries]);
    setSelectedJob('');
    setHours('');
    setDescription('');
  };

  const calculateTotalHours = () => {
    return recentEntries?.reduce((sum, entry) => sum + entry?.hours, 0);
  };

  const calculateTotalEarnings = () => {
    return recentEntries?.reduce((sum, entry) => sum + entry?.earnings, 0);
  };

  return (
    <div 
      className="bg-white border-2 border-slate-200 rounded-lg p-4 md:p-6"
      style={{
        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Clock" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
            Tijd Registratie
          </h3>
          <p className="caption text-muted-foreground">
            Log uw gewerkte uren per opdracht
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 mb-6">
        <Select
          label="Selecteer Opdracht"
          placeholder="Kies een opdracht"
          options={jobOptions}
          value={selectedJob}
          onChange={setSelectedJob}
          searchable
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Datum"
            type="date"
            value={date}
            onChange={(e) => setDate(e?.target?.value)}
            required
          />

          <Input
            label="Aantal Uren"
            type="number"
            placeholder="0.0"
            value={hours}
            onChange={(e) => setHours(e?.target?.value)}
            min="0.5"
            max="24"
            step="0.5"
            required
          />
        </div>

        <Input
          label="Beschrijving (optioneel)"
          type="text"
          placeholder="Wat heeft u gedaan?"
          value={description}
          onChange={(e) => setDescription(e?.target?.value)}
        />

        <Button type="submit" variant="default" fullWidth iconName="Plus" iconPosition="left">
          Tijd Toevoegen
        </Button>
      </form>
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-sm md:text-base text-foreground">
            Recente Registraties
          </h4>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="caption text-muted-foreground">Totaal Uren</p>
              <p className="font-semibold text-sm text-foreground">{calculateTotalHours()?.toFixed(1)}u</p>
            </div>
            <div className="text-right">
              <p className="caption text-muted-foreground">Totaal Verdiensten</p>
              <p className="font-semibold text-sm text-accent">
                €{calculateTotalEarnings()?.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3 max-h-64 overflow-y-auto">
          {recentEntries?.map((entry) => (
            <div key={entry?.id} className="bg-muted/50 rounded-lg p-3 border border-border">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground line-clamp-1">
                    {entry?.jobTitle}
                  </p>
                  {entry?.description && (
                    <p className="caption text-muted-foreground line-clamp-1">
                      {entry?.description}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="font-semibold text-sm text-accent whitespace-nowrap">
                    €{entry?.earnings?.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="Calendar" size={12} />
                  <span>{new Date(entry.date)?.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Clock" size={12} />
                  <span>{entry?.hours}u</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="DollarSign" size={12} />
                  <span>€{entry?.rate?.toFixed(2)}/u</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimeTrackingWidget;